import { musician } from '@/mocks/musician';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const timelineIcons = [
  'ri-music-2-line',
  'ri-heart-3-line',
  'ri-building-line',
  'ri-book-open-line',
  'ri-home-heart-line',
  'ri-group-line',
  'ri-parent-line',
];

const accomplishmentCategories = ['Choirs', 'Awards', 'Musical Achievements'] as const;

export default function Experience() {
  useScrollAnimation();

  return (
    <>
      <title>Experience &amp; Service | Jackson Lacefield | Tenor &amp; Worship Musician</title>
      <meta name="description" content="Explore Jackson Lacefield's journey of worship leadership, service, and musical achievement — including Baylor University, church worship teams, scholarships, and classical vocal performance." />
      <meta name="keywords" content="Jackson Lacefield, experience, worship leader, Baylor University, church music, TMEA All-State, scholarships, classical tenor repertoire" />
      <link rel="canonical" href="https://www.jacksonlacefield.com/experience" />
      <meta property="og:title" content="Experience &amp; Service | Jackson Lacefield | Tenor &amp; Worship Musician" />
      <meta property="og:description" content="Jackson Lacefield's journey of worship leadership, service, and musical achievement." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://www.jacksonlacefield.com/experience" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content="Experience &amp; Service | Jackson Lacefield" />
      <meta name="twitter:description" content="Jackson Lacefield's journey of worship leadership, service, and musical achievement." />
      <main className="min-h-screen bg-background-50">
      {/* ── Page Header ── */}
      <section className="page-header gradient-section-orange film-grain">
        <div className="section-container text-center relative z-10">
          <span className="inline-block font-label text-xs tracking-widest uppercase text-accent-500 mb-4 scroll-animate">
            Experience
          </span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold text-foreground-900 mb-3 scroll-animate">
            Journey of Serving
          </h1>
          <p className="font-body text-sm md:text-base text-foreground-500 max-w-lg mx-auto scroll-animate">
            A path of worship, leadership, and service through music and ministry.
          </p>
        </div>
      </section>

      {/* ── Timeline ── */}
      <section className="py-14 md:py-24 gradient-section film-grain section-popout">
        <div className="section-container relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="mb-10 md:mb-14 scroll-animate">
              <span className="inline-block font-label text-xs tracking-widest uppercase text-accent-500 mb-3">
                Service &amp; Leadership
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground-900">
                Where I Have Served
              </h2>
            </div>

            <div className="relative scroll-animate">
              <div className="absolute left-5 sm:left-6 md:left-8 top-0 bottom-0 w-px bg-accent-200/60"></div>

              <div className="space-y-6 md:space-y-8">
                {musician.selectedRoles.map((item, i) => (
                  <div
                    key={i}
                    className="relative flex gap-3 sm:gap-4 md:gap-6 scroll-animate"
                    style={{ transitionDelay: `${i * 0.1}s` }}
                  >
                    <div className="relative z-10 flex-shrink-0">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full bg-background-50 border border-accent-200 flex items-center justify-center text-accent-500 transition-all duration-500 hover:bg-accent-500 hover:text-background-50 hover:border-accent-500 hover:scale-110">
                        <i className={`${timelineIcons[i % timelineIcons.length]} text-base sm:text-lg md:text-xl`}></i>
                      </div>
                    </div>

                    <div className="flex-1 pt-1 md:pt-2">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-1.5">
                        <h3 className="font-heading text-base sm:text-lg md:text-xl font-semibold text-foreground-900">
                          {item.role}
                        </h3>
                        <span className="font-label text-xs text-accent-500 whitespace-nowrap">
                          {item.period}
                        </span>
                      </div>
                      <p className="font-body text-sm text-foreground-500">{item.organization}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Accomplishments ── */}
      <section className="py-14 md:py-24 section-dark film-grain">
        <div className="section-container">
          <div className="mb-10 md:mb-14 scroll-animate">
            <span className="inline-block font-label text-xs tracking-widest uppercase text-accent-400 mb-3">
              Accomplishments
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-2">
              Recognitions &amp; Achievements
            </h2>
          </div>

          <div className="trinity-grid">
            {accomplishmentCategories.map((cat, i) => (
              <div
                key={cat}
                className="popout-card p-5 md:p-7 scroll-animate"
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center text-accent-500">
                    <i className="ri-award-line text-base"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900">{cat}</h3>
                </div>
                <div className="space-y-3">
                  {musician.accomplishments
                    .filter((a) => a.category === cat)
                    .map((a, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="w-1.5 h-1.5 rounded-full bg-accent-400 mt-2 flex-shrink-0"></div>
                        <p className="font-body text-sm text-foreground-600">{a.title}</p>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Scholarships ── */}
      <section className="py-12 md:py-18 gradient-section-mid film-grain section-popout">
        <div className="section-container">
          <div className="mb-7 md:mb-9 scroll-animate">
            <span className="inline-block font-label text-xs tracking-widest uppercase text-accent-500 mb-3">
              Scholarships &amp; Honors
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 dark:text-white">
              Academic Recognition
            </h2>
          </div>
          <div className="flex flex-wrap gap-3 scroll-animate">
            {musician.scholarships.map((item, i) => (
              <span
                key={i}
                className="bg-accent-100 text-accent-800 font-label text-sm px-5 py-2.5 rounded-full whitespace-nowrap popout-card"
                style={{ transitionDelay: `${i * 0.05}s` }}
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Repertoire ── */}
      <section className="py-12 md:py-18 section-grey film-grain">
        <div className="section-container">
          <div className="mb-7 md:mb-9 scroll-animate">
            <span className="inline-block font-label text-xs tracking-widest uppercase text-accent-500 mb-3">
              Repertoire
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 dark:text-white">
              Musical Range
            </h2>
          </div>
          <div className="flex flex-wrap gap-3 scroll-animate">
            {musician.repertoire.map((item, i) => (
              <span
                key={i}
                className="bg-background-50 text-foreground-700 dark:text-foreground-900 font-label text-xs md:text-sm px-4 py-2.5 rounded-full popout-card"
                style={{ transitionDelay: `${i * 0.03}s` }}
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>
    </main>
    </>
  );
}